import { prorateFirstCharge } from '@/services/billing/proration';
import type { CheckoutDiscountSummary } from '@/services/billing/buildDiscount';
import type { Sport, SubscriptionProduct } from '@sharpr/types';
import type { SignupPlanType } from '@/lib/posthog/events';

export interface SignupSummaryProduct {
  sport: Sport;
  name: string;
  monthly_price_cents: number;
  first_charge_cents?: number;
  /** Season-tail signup: $0 today, born paused, first charge at next season's resume. */
  season_tail?: true;
}

export interface SignupSummary {
  products: SignupSummaryProduct[];
  trial_offered: boolean;
  trial_days: number;
  trial_end?: number;
  /** Pre-sale: the season opener (unix seconds) this signup waits for. Present only when
   *  that is after signup. With a trial, the trial runs trial_days from here; without one,
   *  nothing is charged today and the first full month is charged here. */
  coverage_start?: number;
  discount?: CheckoutDiscountSummary;
}

export interface BuildSignupSummaryArgs {
  products: SubscriptionProduct[];
  trialOffered: boolean;
  trialDays: number;
  /** Stable wall-clock anchor (ms). Passed once so summary + metadata stamps agree across
   *  the multi-step flow (checkout-setup at T, record-consent recompute at T+δ must match). */
  nowMs: number;
  discount?: CheckoutDiscountSummary;
  /** Add-mode: per-sport inherited renewal anchor. When set, first_charge prorates to the
   *  anchor. Fresh/cohort signup leaves empty — long-lived subs carry no season-end
   *  cancel_at, so the first charge is a full month. */
  perSportAnchorUnix?: Map<Sport, number>;
  /** Sports in the season-tail window: first charge is $0 (sub is created paused;
   *  the season-billing cron bills month-one at next season's resume). */
  tailSports?: Set<Sport>;
  /** Add-mode mid-trial: cohort's inherited trial_end. Overrides the computed trial end for
   *  BOTH summary.trial_end and the proration start (else first_charge prorates from a
   *  fresh-trial date → wrong disclosed amount). */
  inheritedTrialEndUnix?: number;
  /** Pre-sale: the opener the cart waits for (`pendingCoverageStartMs`); absent = starts now.
   *  Ignored when `inheritedTrialEndUnix` is set (the cohort's trial wins). With a trial,
   *  the trial starts there; without one, billing does (full month, no anchor proration). */
  coverageStartMs?: number;
}

/** THE tail-vs-trial policy, single home (checkout-setup decides with it,
 *  PricingPage renders with it — they cannot drift). A season-tail sport is
 *  free-rest-of-season (no separate trial); a NON-tail sport in the same cart
 *  keeps the normal trial. So the trial is offered when the user is eligible AND
 *  the cart isn't entirely tail. (A tail-only cart still consumes trial_used at
 *  finalize — the free tail IS that user's trial.) */
export function offerTrialForCart(
  trialEligible: boolean,
  sports: ReadonlyArray<Sport>,
  tailSports: ReadonlySet<Sport>
): boolean {
  return trialEligible && sports.some((s) => !tailSports.has(s));
}

/** How a signup waits for the opener — the one classifier every display surface reads
 *  (order summary, pricing, disclosure, Apple Pay, success page). `trial`: the trial starts
 *  at `startMs`; `billing`: nothing today, the first full month is charged at `startMs`. */
export type OpenerWait = { kind: 'trial' | 'billing'; startMs: number };

export function openerWait(trialOffered: boolean, startMs: number | null | undefined): OpenerWait | null {
  return startMs == null ? null : { kind: trialOffered ? 'trial' : 'billing', startMs };
}

/** Pre-sale without a trial: the sub's billing anchor (unix seconds) — $0 today, the full
 *  month on opening night. Derived from the summary so the opener date has one home. */
export function deferredBillingStartUnix(
  summary: Pick<SignupSummary, 'trial_offered' | 'coverage_start'>,
): number | undefined {
  return summary.trial_offered ? undefined : summary.coverage_start;
}

/** Analytics plan type — single precedence for checkout-setup and finalize. */
export function signupPlanType(args: {
  trial: boolean;
  deferredToOpener: boolean;
  anyTail: boolean;
}): SignupPlanType {
  if (args.trial) return 'trial';
  if (args.deferredToOpener) return 'presale';
  if (args.anyTail) return 'season_tail';
  return 'paid_immediate';
}

export function buildSignupSummary(
  args: BuildSignupSummaryArgs,
): SignupSummary & { trialEndUnix?: number } {
  const { products, trialOffered, trialDays, nowMs, discount } = args;
  const coverageStartMs = Math.max(nowMs, args.coverageStartMs ?? nowMs);
  const presaleStartUnix =
    args.inheritedTrialEndUnix == null && coverageStartMs > nowMs
      ? Math.floor(coverageStartMs / 1000)
      : undefined;
  // No trial + pre-sale: the sub is born with its billing anchor on the opener and no
  // proration — $0 today, the full month on opening night.
  const deferredToOpener = !trialOffered && presaleStartUnix != null;
  const trialEndUnix = trialOffered
    ? (args.inheritedTrialEndUnix
      ?? Math.floor((coverageStartMs + trialDays * 24 * 60 * 60 * 1000) / 1000))
    : undefined;
  const firstChargeStart = trialEndUnix ? new Date(trialEndUnix * 1000) : new Date(nowMs);

  const summaryProducts: SignupSummaryProduct[] = products.map((p) => {
    const sport = p.sport as Sport;
    if (args.tailSports?.has(sport)) {
      return {
        sport,
        name: p.name,
        monthly_price_cents: p.monthly_price_cents,
        first_charge_cents: 0,
        season_tail: true as const,
      };
    }
    const anchorUnix = deferredToOpener ? undefined : args.perSportAnchorUnix?.get(sport);
    const firstCharge = anchorUnix
      ? prorateFirstCharge(p.monthly_price_cents, firstChargeStart, new Date(anchorUnix * 1000))
      : p.monthly_price_cents;
    return {
      sport,
      name: p.name,
      monthly_price_cents: p.monthly_price_cents,
      ...(firstCharge !== p.monthly_price_cents ? { first_charge_cents: firstCharge } : {}),
    };
  });

  // Inherited trial_end → trial_days = REMAINING days, not configured trialDays.
  const finalTrialDays = trialOffered
    ? (args.inheritedTrialEndUnix
      ? Math.max(0, Math.ceil((args.inheritedTrialEndUnix - Math.floor(nowMs / 1000)) / 86400))
      : trialDays)
    : 0;

  return {
    products: summaryProducts,
    trial_offered: trialOffered,
    trial_days: finalTrialDays,
    ...(trialEndUnix ? { trial_end: trialEndUnix } : {}),
    ...(presaleStartUnix ? { coverage_start: presaleStartUnix } : {}),
    ...(discount ? { discount } : {}),
    trialEndUnix,
  };
}
